import { BAR_SLOTS } from "odyssey-shared";
import { BAR_PAGE_SIZE, pageColumn, pageOf, type BarPages } from "./pages.ts";

/**
 * How the page shown follows the bar's arranging where the bar pages (P2.8-T30, ADR-121 decision 8 and its note of
 * 2026-10-12):
 *
 * - **Placing.** The page shown follows placing's focus, so the arrows, Home and End reach all ten slots, and a page
 *   turned under placing (the swap control or its key) moves the focus to the same place on the page now shown.
 * - **A drag.** A drag resting on the swap control turns the page (`swap-hold.ts`). A drag that is cancelled, by
 *   Escape, a lost window or a release on the control itself, turns the bar back to the page it started on, so the
 *   thing that went back to its slot is in sight; a drop that lands leaves the page it landed on.
 *
 * Where the bar shows all ten, or nothing pages it, every answer here changes nothing.
 */

/**
 * The bar's pages as arranging reads and turns them.
 */
export type ArrangingPages = Pick<BarPages, "getSnapshot" | "subscribe" | "show" | "swap">;

/**
 * The place a drag's mark names for the swap control: one past the bar's last slot.
 */
export const SWAP_TARGET = BAR_SLOTS;

/**
 * The page's following of one bar's arranging.
 */
export class PageFollow {
  private started: number | null = null;

  /**
   * @param pages The bar's pages, or null where the bar never pages.
   * @param focused The slot placing focuses now, or null while nothing is placed.
   * @param refocus Moves placing's focus to a slot.
   */
  public constructor(
    private readonly pages: ArrangingPages | null,
    private readonly focused: () => number | null,
    private readonly refocus: (slot: number) => void,
  ) {
    pages?.subscribe(() => {
      const focus = this.focused();
      if (focus !== null && this.focusAfterTurn(focus) !== focus) {
        this.refocus(this.focusAfterTurn(focus));
      }
    });
  }

  /**
   * Shows the other page for a drag resting on the swap control, remembering the page the drag started on.
   *
   * @returns True when the page changed; false where the bar shows all ten.
   */
  public turnForDrag(): boolean {
    const pages = this.pages;
    if (pages === null) {
      return false;
    }
    const before = pages.getSnapshot().page;
    if (!pages.swap()) {
      return false;
    }
    this.started ??= before;
    return true;
  }

  /**
   * A drag ended with its drop taken or put back by its own surface: the page stays.
   */
  public dragLanded(): void {
    this.started = null;
  }

  /**
   * A drag was cancelled: the bar shows the page the drag started on again.
   */
  public dragCancelled(): void {
    const started = this.started;
    this.started = null;
    if (started !== null) {
      this.pages?.show(started);
    }
  }

  /**
   * Shows the page placing's focus is on, as the focus moves.
   */
  public showFocus(): void {
    const focus = this.focused();
    if (focus !== null) {
      this.pages?.show(pageOf(focus));
    }
  }

  /**
   * Where placing's focus stands after the page turned: the same place on the page now shown when the page no longer
   * shows it, and where it was otherwise.
   *
   * @param focus The slot focused, from 0.
   * @returns The slot to focus.
   * @example
   * follow.focusAfterTurn(2);
   * // 7 once a paged bar shows slots 6 to 10
   */
  public focusAfterTurn(focus: number): number {
    const pages = this.pages?.getSnapshot();
    if (pages === undefined || !pages.paged || pageOf(focus) === pages.page) {
      return focus;
    }
    return pages.page * BAR_PAGE_SIZE + pageColumn(focus);
  }
}
